import { inject, Injectable } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'

import { ChargeDescriptionMaster } from '../model/cdm'
import { EstimateCharge, EstimateChargeDiscountType } from '../model/estimate'
import {
  ChargeRowFormGroup,
  ChargeRowFormValue,
  ChargesFormArray,
} from '../model/estimate/charge-form.types'

/**
 * Service for managing charge form arrays in estimate-related components.
 * Provides utilities for creating, converting, and manipulating charge form groups.
 */
@Injectable({
  providedIn: 'root',
})
export class ChargeFormService {
  private readonly fb = inject(FormBuilder)

  /**
   * Creates a FormArray of charge row form groups from an array of EstimateCharge objects.
   * @param charges Array of EstimateCharge objects to convert
   * @returns FormArray containing FormGroups for each charge row
   */
  createChargesFormArray(charges: EstimateCharge[]): ChargesFormArray {
    const chargeGroups = charges.map(charge => this.createChargeFormGroup(charge))
    return this.fb.array(chargeGroups)
  }

  /**
   * Creates a single charge row FormGroup from an EstimateCharge object.
   * @param charge The EstimateCharge to convert
   * @returns FormGroup configured for a charge row
   */
  createChargeFormGroup(charge: Partial<EstimateCharge>): FormGroup<ChargeRowFormGroup> {
    const group: FormGroup<ChargeRowFormGroup> = this.fb.group({
      chargeId: this.fb.control(charge.chargeId || ''),
      chargeCode: this.fb.control(charge.chargeCode || ''),
      type: this.fb.control<ChargeDescriptionMaster.TYPE>({ value: charge.type, disabled: true }, Validators.required),
      description: this.fb.control({ value: charge.description || '', disabled: true }, Validators.required),
      cptCode: this.fb.control({ value: charge.cptCode || '', disabled: true }, Validators.required),
      modifiers: this.fb.control(charge.modifiers?.join(',') || ''),
      revCode: this.fb.control({ value: charge.revCode || '', disabled: true }),
      quantity: this.fb.control(charge.quantity ?? 1, Validators.required),
      unitCharge: this.fb.control({ value: charge.unitCharge || 0, disabled: true }),
      totalCharge: this.fb.control({ value: charge.unitCharge || 0, disabled: true }),
      facilityId: this.fb.control(charge.facilityId || ''),
      visitNumbers: this.fb.control(
        charge.visitNumbers ? charge.visitNumbers : [1],
        Validators.required,
      ),
    })

    group.addControl('code', this.fb.control(charge.code || ''))

    if (charge.cptDiscountAmount != null || charge.cptDiscountType != null) {
      group.addControl('cptDiscount', this.fb.control(charge.cptDiscountAmount ?? 0))
      group.addControl('discountType', this.fb.control(charge.cptDiscountType))
      group.addControl('discountedUnits', this.fb.control(charge.cptDiscountedUnits ?? null))
    }

    return group
  }

  /**
   * Populates a charges FormArray with EstimateCharge objects.
   * Clears existing contents before adding new charges.
   * @param chargesFormArray The FormArray to populate
   * @param charges Array of EstimateCharge objects
   */
  setChargesOnFormArray(chargesFormArray: ChargesFormArray, charges: EstimateCharge[]): void {
    chargesFormArray.clear()
    for (const charge of charges) {
      chargesFormArray.push(this.createChargeFormGroup(charge))
    }
  }

  /**
   * Converts form charge values back to EstimateCharge array.
   * Transforms string fields (modifiers, visitNumbers) back to arrays.
   * @param chargesFormArray The FormArray to convert
   * @returns Array of EstimateCharge objects
   */
  getChargesFromFormArray(chargesFormArray: ChargesFormArray): EstimateCharge[] {
    const formCharges = chargesFormArray.getRawValue() as ChargeRowFormValue[]
    return this.convertFormValuesToEstimateCharges(formCharges)
  }

  /**
   * Converts raw form values to EstimateCharge array.
   * @param formCharges The raw form values
   * @returns Array of EstimateCharge objects
   */
  convertFormValuesToEstimateCharges(formCharges: ChargeRowFormValue[]): EstimateCharge[] {
    return formCharges.map(charge => {
      const modifiers = charge.modifiers
        ? charge.modifiers.split(',').map(m => m.trim().toUpperCase()).filter(Boolean)
        : []

      const estimateCharge: EstimateCharge = {
        chargeId: charge.chargeId || undefined,
        chargeCode: charge.chargeCode ?? '',
        code: charge.code || undefined,
        type: charge.type as EstimateCharge['type'],
        description: charge.description || undefined,
        cptCode: charge.cptCode || undefined,
        modifiers,
        quantity: charge.quantity ?? 1,
        unitCharge: charge.unitCharge ?? 0,
        facilityId: charge.facilityId || undefined,
        visitNumbers: charge?.visitNumbers?.length ? charge.visitNumbers : [1],
        cptDiscountType: charge.discountType,
        cptDiscountAmount: charge.cptDiscount,
        cptDiscountedUnits: charge.discountedUnits,
      }

      if (charge.revCode) {
        estimateCharge.revCode = charge.revCode
      }

      return estimateCharge
    })
  }

  /**
   * Adds discount form controls to a charge at the specified index.
   * @param chargesFormArray The FormArray containing the charges
   * @param index The index of the charge in the charges array
   * @param discountType The type of discount ('percentage' or 'fixed')
   */
  addDiscountControls(
    chargesFormArray: ChargesFormArray,
    index: number,
    discountType: EstimateChargeDiscountType,
  ): void {
    const control = chargesFormArray.at(index)
    if (control) {
      if (!control.contains('cptDiscount')) {
        control.addControl('cptDiscount', this.fb.control(0))
      }
      if (!control.contains('discountType')) {
        control.addControl('discountType', this.fb.control(discountType))
      } else {
        control.get('discountType')?.setValue(discountType)
      }
      if (!control.contains('discountedUnits')) {
        control.addControl('discountedUnits', this.fb.control<number | null>(null))
      }
    }
  }

  /**
   * Removes discount form controls from a charge at the specified index.
   * @param chargesFormArray The FormArray containing the charges
   * @param index The index of the charge in the charges array
   */
  removeDiscountControls(chargesFormArray: ChargesFormArray, index: number): void {
    const control = chargesFormArray.at(index)
    if (control) {
      if (control.contains('cptDiscount')) {
        control.removeControl('cptDiscount')
      }
      if (control.contains('discountType')) {
        control.removeControl('discountType')
      }
      if (control.contains('discountedUnits')) {
        control.removeControl('discountedUnits')
      }
    }
  }

  /**
   * Ensures all charges have visit numbers, providing a fallback when missing.
   * This is primarily a safety mechanism - charges should already have visit numbers
   * from the appointment selection flow (which preserves actual visit numbers like [1, 2, 4]).
   *
   * @param charges Array of EstimateCharge objects
   * @param selectedAppointments Either the actual visit numbers of the selected appointments, or a
   *   count of selected appointments to derive a sequential fallback from
   * @returns Array of EstimateCharge objects with guaranteed visitNumbers
   *
   * @example
   * // Charge already has visit numbers from appointment selection - preserved as-is
   * ensureVisitNumbers([{ visitNumbers: [1, 2, 4], ... }], 3)
   * // Returns: [{ visitNumbers: [1, 2, 4], ... }]
   *
   * @example
   * // Charge missing visit numbers - fallback generates sequential [1, 2, 3]
   * ensureVisitNumbers([{ visitNumbers: [], ... }], 3)
   * // Returns: [{ visitNumbers: [1, 2, 3], ... }]
   *
   * @example
   * // Explicit visit numbers - used verbatim, so appointments not starting at 1 are honoured
   * ensureVisitNumbers([{ visitNumbers: [], ... }], [3])
   * // Returns: [{ visitNumbers: [3], ... }]
   */
  ensureVisitNumbers(charges: EstimateCharge[] = [], selectedAppointments?: number | number[]): EstimateCharge[] {
    const defaultVisitNumbers = this.resolveDefaultVisitNumbers(selectedAppointments)

    return charges.map(c => ({
      ...c,
      visitNumbers: c.visitNumbers && c.visitNumbers.length > 0 ? c.visitNumbers : defaultVisitNumbers,
    }))
  }

  private resolveDefaultVisitNumbers(selectedAppointments?: number | number[]): number[] {
    if (Array.isArray(selectedAppointments)) {
      return selectedAppointments.length > 0 ? [...selectedAppointments] : [1]
    }

    return selectedAppointments && selectedAppointments > 1
      ? Array.from({ length: selectedAppointments }, (_, i) => i + 1)
      : [1]
  }
}
